import { useEffect, useState } from 'react';
import { AlertCircle, Loader2, Mail, MessageSquare, RefreshCw, Search } from 'lucide-react';
import {
  DEMO_REQUEST_STATUSES,
  DEMO_REQUEST_STATUS_LABELS,
  DEMO_SECTOR_LABELS,
  type DemoRequestStatus,
  type DemoSector,
} from '@mygmao/shared';
import { api, extractErrorMessage } from '../../lib/api';
import { showError, showSuccess } from '../../lib/toast';
import { usePermissions } from '../../hooks/usePermissions';

/**
 * Page super-admin `/platform/demo-requests`.
 *
 * Liste les demandes de démo issues du formulaire public de la landing
 * (`POST /demo-requests`). Recherche, filtre statut, et mise à jour du
 * statut de suivi commercial. Réservée au super-admin plateforme.
 */

interface DemoRequestRow {
  id: string;
  full_name: string;
  email: string;
  organization: string | null;
  phone: string | null;
  sector: DemoSector | null;
  message: string | null;
  statut: DemoRequestStatus;
  source: string;
  created_at: string;
}

const STATUT_FILTERS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Tous les statuts' },
  ...DEMO_REQUEST_STATUSES.map((s) => ({ value: s, label: DEMO_REQUEST_STATUS_LABELS[s] })),
];

export function DemoRequestsPage() {
  const { isPlatformAdmin } = usePermissions();

  const [items, setItems] = useState<DemoRequestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statut, setStatut] = useState('');
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  async function refresh() {
    setLoading(true);
    setErrorMessage(null);
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (statut) params.set('statut', statut);
      const res = await api.get<{ data: DemoRequestRow[] }>(
        `/platform/demo-requests${params.toString() ? `?${params}` : ''}`,
      );
      setItems(res.data.data);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!isPlatformAdmin) return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, statut, isPlatformAdmin]);

  async function handleStatusChange(row: DemoRequestRow, next: DemoRequestStatus) {
    setUpdatingId(row.id);
    try {
      await api.patch(`/platform/demo-requests/${row.id}/status`, { statut: next });
      setItems((prev) => prev.map((r) => (r.id === row.id ? { ...r, statut: next } : r)));
      showSuccess('Statut mis à jour');
    } catch (err) {
      showError(extractErrorMessage(err));
    } finally {
      setUpdatingId(null);
    }
  }

  if (!isPlatformAdmin) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <p className="text-sm text-gray-500">
          Cette page est réservée aux administrateurs plateforme.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Mail className="w-7 h-7 mr-2 text-primary-600" />
            Demandes de démo
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Soumissions du formulaire public — {items.length} demande(s).
          </p>
        </div>
        <button
          onClick={refresh}
          className="inline-flex items-center p-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50"
          title="Rafraîchir"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      {/* Toolbar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 flex items-center justify-between flex-wrap gap-3">
        <select
          value={statut}
          onChange={(e) => setStatut(e.target.value)}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          {STATUT_FILTERS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher (nom / email / organisation)…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg w-72 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Demandeur
                </th>
                <th className="hidden md:table-cell px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Organisation
                </th>
                <th className="hidden lg:table-cell px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Secteur
                </th>
                <th className="hidden xl:table-cell px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Reçue le
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center">
                    <Loader2 className="w-6 h-6 animate-spin text-primary-500 mx-auto" />
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-sm text-gray-500">
                    Aucune demande de démo pour le moment.
                  </td>
                </tr>
              ) : (
                items.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50 align-top">
                    <td className="px-3 py-2.5">
                      <div className="font-medium text-gray-900">{r.full_name}</div>
                      <a
                        href={`mailto:${r.email}`}
                        className="text-xs text-primary-600 hover:underline"
                      >
                        {r.email}
                      </a>
                      {r.phone && <div className="text-xs text-gray-500 mt-0.5">{r.phone}</div>}
                      {r.message && (
                        <div className="mt-1 flex items-start text-xs text-gray-500 max-w-xs">
                          <MessageSquare className="w-3 h-3 mr-1 mt-0.5 flex-shrink-0" />
                          <span className="line-clamp-2">{r.message}</span>
                        </div>
                      )}
                    </td>
                    <td className="hidden md:table-cell px-3 py-2.5 text-gray-700">
                      {r.organization ?? '—'}
                    </td>
                    <td className="hidden lg:table-cell px-3 py-2.5 text-gray-600">
                      {r.sector ? DEMO_SECTOR_LABELS[r.sector] : '—'}
                    </td>
                    <td className="hidden xl:table-cell px-3 py-2.5 text-gray-500 text-xs whitespace-nowrap">
                      {new Date(r.created_at).toLocaleString()}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <StatutBadge statut={r.statut} />
                        <select
                          value={r.statut}
                          disabled={updatingId === r.id}
                          onChange={(e) =>
                            handleStatusChange(r, e.target.value as DemoRequestStatus)
                          }
                          className="px-2 py-1 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50"
                          title="Changer le statut"
                        >
                          {DEMO_REQUEST_STATUSES.map((s) => (
                            <option key={s} value={s}>
                              {DEMO_REQUEST_STATUS_LABELS[s]}
                            </option>
                          ))}
                        </select>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const STATUT_STYLES: Record<DemoRequestStatus, string> = {
  nouveau: 'bg-blue-100 text-blue-800 border-blue-200',
  contacte: 'bg-amber-100 text-amber-800 border-amber-200',
  qualifie: 'bg-purple-100 text-purple-800 border-purple-200',
  converti: 'bg-green-100 text-green-800 border-green-200',
  clos: 'bg-gray-100 text-gray-700 border-gray-200',
};

function StatutBadge({ statut }: { statut: DemoRequestStatus }) {
  return (
    <span
      className={`hidden sm:inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium border ${STATUT_STYLES[statut]}`}
    >
      {DEMO_REQUEST_STATUS_LABELS[statut]}
    </span>
  );
}
